import {cn} from "@/lib/utils.ts";
import {Link} from "@tanstack/react-router";
import logoScorePal from "../assets/herder_logo.png";
import ArrowUpRightIcon from "../icons/ArrowUpRightIcon.tsx";
import MobileMenu from "./MobileMenu.tsx";

interface LandingNavProps {
    activePage?: 'home' | 'about' | 'features' | 'pricing' | 'blog' | 'contact' | 'leaderboards';
}

const LandingNav = ({activePage}: LandingNavProps) => {
    const linkClass = (page: string) => {
        const isActive = activePage === page;
        return `hover:text-[#EDEEF4] transition ${isActive ? 'font-semibold underline underline-offset-4' : ''}`;
    };

    return (
        <div data-cy="landing-nav" className="flex justify-between items-center px-2 xl:px-6 xl:pt-6 pb-4">
            <nav className="max-xl:hidden flex items-center gap-5 font-inter text-white text-base">
                <Link to="/" className={linkClass('home')}>
                    Home
                </Link>
                <Link to="/about/" className={linkClass('about')}>
                    About Us
                </Link>
                <div className={"cursor-pointer relative group z-10"}>
                    <Link to="/" className={linkClass('features')}>
                        Features
                    </Link>

                    <div className={"-left-4 p-4 absolute z-10 pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100 transition-all"}>
                        <div className={"bg-[#212226]/95 border border-white/20 p-4 whitespace-nowrap rounded-xl flex gap-2 min-w-72"}>
                            <div className={"flex flex-col gap-2 text-medium"}>
                                <Link to="/create-brackets/" className={cn(linkClass('create-brackets'), "hover:text-secondary")}>
                                    Create Tournament Brackets
                                </Link>
                                <Link to="/sports-tournament-management-software/" className={cn(linkClass('sports-tournament-management-software'), "hover:text-secondary")}>
                                    Sports Tournament Management
                                </Link>
                                <Link to="/sports-league-management-software/" className={cn(linkClass('sports-league-management-software'), "hover:text-secondary")}>
                                    Sports League Management
                                </Link>
                                <Link to="/sports-analytics-software-for-clubs/" className={cn(linkClass('sports-analytics-software-for-clubs'), "hover:text-secondary")}>
                                    Sports Analytics
                                </Link>
                                <Link to="/live-scoring/" className={cn(linkClass('live-scoring'), "hover:text-secondary")}>
                                    Live Scoring
                                </Link>
                                <Link to="/for-clubs/" className={cn(linkClass('for-clubs'), "hover:text-secondary")}>
                                    For Clubs
                                </Link>
                                <Link to="/for-communities/"className={cn(linkClass('for-communities'), "hover:text-secondary")}>
                                    For Communities
                                </Link>

                                <div className={"flex text-white/40 py-2 text-small items-baseline"}>
                                    <div className={"block border-t-1 border-white/40 w-full h-1"}></div>
                                    <div className={"px-1"}>SPORTS</div>
                                    <div className={"block border-t-1 border-white/40 w-full h-1"}></div>
                                </div>

                                <div className={"flex flex-wrap justify-around gap-2"}>
                                    <Link to="/padel-tournament-software/" className={cn(linkClass('padel-tournament-software'), "hover:text-secondary")}>
                                        Padel
                                    </Link>
                                    <Link to="/pickleball-tournament-software/" className={cn(linkClass('pickleball-tournament-software'), "hover:text-secondary")}>
                                        Pickleball
                                    </Link>
                                    <Link to="/tennis-tournament-software/" className={cn(linkClass('tennis-tournament-software'), "hover:text-secondary")}>
                                        Tennis
                                    </Link>
                                    <Link to="/badminton-tournament-software/" className={cn(linkClass('badminton-tournament-software'), "hover:text-secondary")}>
                                        Badminton
                                    </Link>
                                    <Link to="/squash-tournament-software/" className={cn(linkClass('squash-tournament-software'), "hover:text-secondary")}>
                                        Squash
                                    </Link>
                                    <Link to="/basketball-tournament-software/" className={cn(linkClass('basketball-tournament-software'), "hover:text-secondary")}>
                                        Basketball
                                    </Link>
                                    <Link to="/football-tournament-software/" className={cn(linkClass('football-tournament-software'), "hover:text-secondary")}>
                                        Football
                                    </Link>
                                    <Link to="/esports-tournament-software/" className={cn(linkClass('esports-tournament-software'), "hover:text-secondary")}>
                                        Esports
                                    </Link>
                                    <Link to="/volleyball-tournament-software/" className={cn(linkClass('volleyball-tournament-software'), "hover:text-secondary")}>
                                        Volleyball
                                    </Link>
                                    <Link to="/roundnet-tournament-software/" className={cn(linkClass('roundnet-tournament-software'), "hover:text-secondary")}>
                                        Roundnet
                                    </Link>
                                </div>

                            </div>

                        </div>
                    </div>
                </div>
                <Link
                    to="/"
                    hash="how-it-works"
                    onClick={() => document.getElementById('how-it-works')?.scrollIntoView({behavior: 'smooth'})}
                    className={linkClass('how-it-works')}
                >
                    How it Works
                </Link>
                <div className={"cursor-pointer relative group z-10"}>
                    <span className={linkClass('leaderboards')}>
                        Leaderboards
                    </span>
                    <div className={"-left-4 p-4 absolute z-10 pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100 transition-all"}>
                        <div className={"bg-[#212226]/95 border border-white/20 p-4 whitespace-nowrap rounded-xl flex gap-2 min-w-56"}>
                            <div className={"flex flex-col gap-2 text-medium"}>
                                <Link to="/leaderboards/leagues/" className={cn(linkClass(''), "hover:text-secondary")}>
                                    League of Leagues
                                </Link>
                                <Link to="/leaderboards/players/" className={cn(linkClass(''), "hover:text-secondary")}>
                                    League of Players
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>

            </nav>

            <div className="absolute left-1/2 -translate-x-1/2 top-0">
                <Link to="/">
                    <img src={logoScorePal} alt="ScorePal Logo" className="h-10 xl:h-11 2xl:h-14" />
                </Link>
            </div>

            <div className="max-xl:block xl:hidden ml-auto">
                <MobileMenu />
            </div>

            <div className="max-xl:hidden flex items-center gap-5 ml-auto">
                <Link to="/pricing/" className={`text-white text-base ${linkClass('pricing')}`}>
                    Pricing
                </Link>
                <Link to="/blog/" className={`text-white text-base ${linkClass('blog')}`}>
                    Blog
                </Link>
                <Link to="/contact/" className={`text-white text-base ${linkClass('contact')}`}>
                    Contact
                </Link>

                <div className="flex items-center gap-3 xl:ml-2">
                    <Link
                        to="/login/"
                        className="flex items-center gap-2 bg-transparent font-inter border-1 border-white text-white rounded-full pl-4 pr-1 py-1 text-base font-medium hover:bg-white/10 transition"
                    >
                        <span>Log In</span>
                        <div className="bg-white rounded-full p-1">
                            <ArrowUpRightIcon className="text-[#082F8A]" size={22} />
                        </div>
                    </Link>
                    <Link
                        to={"/signup/player/" as any}
                        className="flex items-center gap-2 bg-[#EDEEF4] font-inter text-[#082F8A] rounded-full pl-4 pr-1 py-1 text-base font-medium hover:bg-white transition"
                    >
                        <span>Sign Up</span>
                        <div className="bg-[#082F8A] rounded-full p-1">
                            <ArrowUpRightIcon className="text-white" size={22} />
                        </div>
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default LandingNav;
